import React, { useCallback } from 'react';

import { Icon } from '@rbilabs/universal-components';
import { useRouter } from 'expo-router';
import { useIntl } from 'react-intl';

import ActionButton, { ActionButtonSizes, IActionButtonProps } from 'components/action-button';
import { useCart } from 'hooks/use-cart';
import useIsSignUpAfterCartEnabled from 'hooks/use-is-sign-up-after-cart';
import { useIsDesktopBreakpoint } from 'hooks/use-media-query';
import { logButtonClick } from 'state/amplitude';
import { ClickEventComponentNames } from 'state/amplitude/constants';
import { useAuthContext } from 'state/auth';
import { RunningExperiments, useExperiments } from 'state/experiments';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { HapticsNotificationType, hapticNotification } from 'utils/haptic';
import { routes } from 'utils/routing';

import { Badge, FabContainer } from './styled';

export interface FabProps extends IActionButtonProps {
  caption?: string;
  badge?: string | number | undefined;
}

const Inner: React.FC<React.PropsWithChildren<FabProps>> = ({
  size = ActionButtonSizes.LARGE,
  caption,
  badge,
  onPress,
  ...rest
}) => (
  <FabContainer>
    {badge && <Badge>{badge}</Badge>}
    <ActionButton
      keepIconSize
      size={size}
      shadow="7"
      paddingLeft="$6"
      paddingRight="$6"
      backgroundColor={Styles.color.primary}
      _text={{ fontSize: 'lg' }}
      _hover={{
        backgroundColor: Styles.color.primaryHover,
      }}
      onPress={onPress}
      {...rest}
    >
      {caption}
    </ActionButton>
  </FabContainer>
);

export const FloatingViewCartFab = () => {
  const { formatMessage } = useIntl();
  const { isAuthenticated, openSignInModal } = useAuthContext();
  const isSignUpAfterCartEnabled = useIsSignUpAfterCartEnabled();
  const enableMainFabButton = useFlag(LaunchDarklyFlag.ENABLE_MAIN_FAB_BUTTON);
  const isDesktop = useIsDesktopBreakpoint();
  const { numberOfItemsInCart } = useCart();
  const router = useRouter();
  const { logExposure } = useExperiments();

  const onPress = useCallback(() => {
    logButtonClick(
      {
        attributes: {
          component: ClickEventComponentNames.BUTTON,
          name: 'View Cart',
        },
      },
      { logDuplicateClickEvent: true }
    );

    if (!isAuthenticated) {
      logExposure(RunningExperiments.SignInUpAfterCart);
    }

    if (isAuthenticated || isSignUpAfterCartEnabled) {
      hapticNotification({ type: HapticsNotificationType.SUCCESS });
      router.navigate(routes.cart);
    } else {
      openSignInModal({
        navigateOnSuccessRoute: routes.cart,
      });
    }
  }, [isAuthenticated, isSignUpAfterCartEnabled, router, openSignInModal, logExposure]);

  if (numberOfItemsInCart < 1 || !enableMainFabButton || isDesktop) {
    return null;
  }

  const fabCaption = formatMessage({ id: 'viewCart' });

  return (
    <Inner
      caption={fabCaption}
      badge={numberOfItemsInCart}
      testID={`nav-${fabCaption}`}
      onPress={onPress}
      leftIcon={<Icon variant={'cart'} size={'sm'} />}
    />
  );
};
